<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          v-if="isPageFilterSelected"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="pagesSelected"
            :pages-items="pagesFormatted"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>
        <v-col
          v-for="(filter, index) in filtersObj.filter(filter => filter.isSelected === true)"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-if="filter.isSelected"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :channels="channels"
            :data-type="card.dataType"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
      <ModalAlert
        v-if="showTikTokAlert"
        :alert-message="alertMessage"
        :link-text="linkText"
        :link-reference="linkReference"
        @close-alert="closeAlert()"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import { DsIcon } from '@ds/icons';
import PageFilter from '@/components/filter/PageFilter';
import NegativationFilter from '@/components/filter/NegativationFilter';
import ReplierStatusFilter from '@/components/filter/ReplierStatusFilter';
import LoversAndHatersFilter from '@/components/filter/LoversAndHatersFilter';
import TextQueryFilter from '@/components/filter/TextQueryFilter';
import RatingFilter from '@/components/filter/RatingFilter';
import PostByIdFilter from '@/components/filter/PostByIdFilter';
import RepliersFilter from '@/components/filter/RepliersFilter';
import ChannelFilter from '@/components/filter/ChannelFilter';
import GenericUserFilter from '@/components/filter/GenericUserFilter';
import PolarityFilter from '@/components/filter/PolarityFilter';
import SacTypeFilter from '@/components/filter/SacTypeFilter';
import filtersModel from '@/mixins/filters-model';
import ModalAlert from '@/components/ModalAlert';
import RepliedFilter from '@/components/filter/RepliedFilter';
import TagView from '@/components/filter/TagView';
import { isEqual, cloneDeep } from 'lodash';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import genericFilterModel from '@/mixins/generic-filter-model';

import { mapGetters } from 'vuex';
import {
  FILTER_NAMES,
} from '@/store/modules/cards/constants';

export default {
  name: 'GenericFilterModal',
  components: {
    StIcon,
    DsIcon,
    PageFilter,
    ReplierStatusFilter,
    TextQueryFilter,
    RatingFilter,
    PostByIdFilter,
    RepliersFilter,
    ModalAlert,
    NegativationFilter,
    ChannelFilter,
    LoversAndHatersFilter,
    GenericUserFilter,
    PolarityFilter,
    SacTypeFilter,
    RepliedFilter,
    TagView,
  },
  mixins: [
    filtersModel,
    genericFilterModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      channels: [],
      users: [],
      changeOnPageFilter: false,
      tikTokAlertRule: false,
      showTikTokAlert: false,
    };
  },
  computed: {
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    filterTitleLabel() {
      const dataTypeLabel = this.$t(`components.filter.ModalFilters.label.dataTypeLabel.${this.card.dataType}`);
      const prefix = this.$t('components.filter.ModalFilters.label.dataTypeLabel.filterPrefix');
      return `${prefix} - ${dataTypeLabel} - ${this.card.props.title}`;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    isPageFilterSelected() {
      const selectedFilters = this.card.props.selectedFilters;
      return selectedFilters.includes(FILTER_NAMES.PAGES);
    },
    filtersObj() {
      const tagItems = [
        {
          text: this.$t('components.OptionsButton.label.notClassifiedLabelPlural'),
          value: 'Sem Tags',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.tags?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const themeItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.generic.notClassified'),
          value: 'Não Classificados',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.themes?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const namesItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.generic.notClassified'),
          value: 'Sem Grupos',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.names?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const selectedFilters = this.card.props.selectedFilters;

      return [
        {
          component: 'RepliersFilter',
          filterType: FILTER_NAMES.SAC_REPLIER,
          isSelected: selectedFilters.includes(FILTER_NAMES.SAC_REPLIER),
          props: {
            selectItems: this.repliersToSelectItem(this.activeDashboardFilters?.repliers),
          },
        },
        {
          component: 'PostByIdFilter',
          filterType: FILTER_NAMES.PIDS,
          isSelected: selectedFilters.includes(FILTER_NAMES.PIDS),
        },
        {
          component: 'RatingFilter',
          filterType: FILTER_NAMES.RATING,
          isSelected: selectedFilters.includes(FILTER_NAMES.RATING),
          props: {
            surveyType: this.card.props.surveyType,
          },
        },
        {
          component: 'TextQueryFilter',
          filterType: FILTER_NAMES.TEXT_QUERY,
          isSelected: selectedFilters.includes(FILTER_NAMES.TEXT_QUERY),
        },
        {
          component: 'ReplierStatusFilter',
          filterType: FILTER_NAMES.SAC_REPLIER_STATUS,
          isSelected: selectedFilters.includes(FILTER_NAMES.SAC_REPLIER_STATUS),
        },
        {
          component: 'ChannelFilter',
          filterType: FILTER_NAMES.CHANNEL,
          isSelected: selectedFilters.includes(FILTER_NAMES.CHANNEL),
        },
        {
          component: 'RepliedFilter',
          filterType: FILTER_NAMES.REPLIED,
          isSelected: selectedFilters.includes(FILTER_NAMES.REPLIED),
        },
        {
          component: 'LoversAndHatersFilter',
          filterType: FILTER_NAMES.LOVERS_HATERS,
          isSelected: selectedFilters.includes(FILTER_NAMES.LOVERS_HATERS),
        },
        {
          component: 'GenericUserFilter',
          filterType: FILTER_NAMES.UIDS,
          isSelected: selectedFilters.includes(FILTER_NAMES.UIDS),
          props: {
            selectItems: this.usersToSelectItem(this.users) || [],
          },
        },
        {
          component: 'PolarityFilter',
          filterType: FILTER_NAMES.POLARITY,
          isSelected: selectedFilters.includes(FILTER_NAMES.POLARITY),
        },
        {
          component: 'SacTypeFilter',
          filterType: FILTER_NAMES.SAC_TYPE,
          isSelected: selectedFilters.includes(FILTER_NAMES.SAC_TYPE),
        },
        {
          component: 'TagView',
          filterType: FILTER_NAMES.TAGS_VIEW,
          isSelected: selectedFilters.includes(FILTER_NAMES.TAGS_VIEW),
          props: {
            selectItems: tagItems,
            searchable: true,
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.TAG,
          isSelected: selectedFilters.includes(FILTER_NAMES.TAG),
          props: {
            label: this.$t('components.filter.ModalFilters.label.generic.tags'),
            selectItems: tagItems,
            searchable: true,
            hasTooltip: true,
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.GROUP,
          isSelected: selectedFilters.includes(FILTER_NAMES.GROUP),
          props: {
            label: this.$t('components.filter.ModalFilters.label.generic.groups'),
            selectItems: namesItems,
            searchable: true,
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.THEME,
          isSelected: selectedFilters.includes(FILTER_NAMES.THEME),
          props: {
            label: this.$t('components.filter.ModalFilters.label.generic.themes'),
            selectItems: themeItems,
            searchable: true,
          },
        },
      ];
    },
    isFilterSelected(filterObj) {
      return this.selectedFilters.includes(filterObj.filterType);
    },
  },
  watch: {
    'tempFilter.pages': {
      handler(newPages, oldPages) {
        if (!isEqual(oldPages, newPages) && Boolean(newPages?.value?.length)) {
          this.channels = Array.from(new Set(this.pagesFormatted
            .filter(({ value }) => newPages?.value.includes(value))
            .map(({ channel }) => channel)));
          this.tempFilter.channels = { value: this.channels };
        }

        if (!isEqual(oldPages, newPages) && !Boolean(newPages?.value?.length)) {
          delete this.tempFilter.channels;
          delete this.tempFilter.pages;
        }
      },
      deep: true,
    },
  },
  created() {
    if ('filter' in this.card.props) {
      this.appliedFilter = cloneDeep(this.card.props.filter);
      this.tempFilter = cloneDeep(this.card.props.filter);
    }

    this.channels = this.tempFilter.channels?.value || [];
    this.users = this.appliedFilter.users?.value || [];
  },
  methods: {
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
